import { FeedbackTopicType } from '@/core/models/feedback-topic'
import useGet from '@/core/composables/api/useGet'
import { computed, onMounted, ref } from 'vue'
import { ApiRequestStatus } from '@/core/models/api/api-request-status'
import type { SurveySelectionPreview } from './use-evaluation-profile'
import type { Perspective } from '@/core/models/perspective'

export function useProfileExportOptions() {
  const {
    data: exportOptions,
    status,
    load,
  } = useGet<ProfileExportOptions>('profile/export/options')

  onMounted(async () => {
    await load()
    if (status.value === ApiRequestStatus.Success && exportOptions.value) {
      initSelection(exportOptions.value)
    }
  })

  const selectedTopicIds = ref<string[]>([])
  const selectedSurveyIdsByTopicId = ref<Record<string, string[]>>({})

  const initSelection = (options: ProfileExportOptions) => {
    selectedTopicIds.value = options.feedbackTopics
      .filter((topic) => topic.type === FeedbackTopicType.Journal || topic.surveys.length > 0)
      .map((topic) => topic.id)
    selectedSurveyIdsByTopicId.value = options.feedbackTopics.reduce<Record<string, string[]>>(
      (result, topic) => {
        if (topic.type === FeedbackTopicType.Evaluation || topic.type === FeedbackTopicType.Pulse) {
          result[topic.id] = topic.surveys?.map((survey) => survey.id) ?? []
        }
        return result
      },
      {},
    )
  }

  const isTopicSelected = (topicId: string) => {
    return selectedTopicIds.value.includes(topicId)
  }

  const toggleTopic = (topicId: string) => {
    if (isTopicSelected(topicId)) {
      selectedTopicIds.value = selectedTopicIds.value.filter((id) => id !== topicId)
      return
    }
    selectedTopicIds.value.push(topicId)
  }

  const isSurveySelected = (topicId: string, surveyId: string) => {
    return selectedSurveyIdsByTopicId.value[topicId]?.includes(surveyId) ?? false
  }

  const toggleSurvey = (topicId: string, surveyId: string) => {
    const selectedSurveyIds = selectedSurveyIdsByTopicId.value[topicId] ?? []
    if (selectedSurveyIds.includes(surveyId)) {
      selectedSurveyIdsByTopicId.value[topicId] = selectedSurveyIds.filter((id) => id !== surveyId)
      return
    }
    selectedSurveyIdsByTopicId.value[topicId] = [...selectedSurveyIds, surveyId]
  }

  const payload = computed<ProfilePdfPayload>(() => {
    const options = exportOptions.value

    return {
      topics: options.feedbackTopics
        .filter((topic) => isTopicSelected(topic.id))
        .map((topic) => {
          if (topic.type !== FeedbackTopicType.Evaluation) {
            return {
              feedbackTopicId: topic.id,
            }
          }

          return {
            feedbackTopicId: topic.id,
            surveyIds: [...(selectedSurveyIdsByTopicId.value[topic.id] ?? [])],
          }
        }),
    }
  })

  const topicPerspectives = computed(() => {
    return exportOptions.value.feedbackTopics
      .map((topic) => {
        if (topic.type === FeedbackTopicType.Journal) {
          return []
        }
        return topic.perspectives
      })
      .flat()
  })

  return {
    exportOptions,
    status,
    toggleTopic,
    isSurveySelected,
    toggleSurvey,
    isTopicSelected,
    payload,
    topicPerspectives,
  }
}

export type ProfilePdfTopicOption =
  | {
      id: string
      title: string
      icon: string
      type: FeedbackTopicType.Journal
    }
  | {
      id: string
      title: string
      icon: string
      type: FeedbackTopicType.Evaluation
      readonly perspectives: ReadonlyArray<Perspective>
      readonly includeSelf: boolean
      readonly surveys: ReadonlyArray<SurveySelectionPreview>
    }
  | {
      id: string
      title: string
      icon: string
      type: FeedbackTopicType.Pulse

      readonly perspectives: ReadonlyArray<Perspective>
      readonly includeSelf: boolean
      readonly surveys: ReadonlyArray<SurveySelectionPreview>
    }

export interface ProfileExportOptions {
  feedbackTopics: ReadonlyArray<ProfilePdfTopicOption>
}

export interface ProfilePdfPayload {
  topics: Array<{
    feedbackTopicId: string
    surveyIds?: string[]
  }>
}
